import { AxiosError } from 'axios'
import { filter, find, noop } from 'lodash'
import { Channel, ChannelState, Socket } from 'phoenix'

import { HttpStatus } from '@shared/models/enums/http'
import { SocketChannels } from '@shared/models/enums/websocket-channels'
import { ConversationChannelEvents } from '@shared/models/enums/websocket-events'
import { Nullable } from '@shared/models/types/primary'
import { isNull } from '@shared/utils/helpers/is-null'

export const SOCKET_CONNECTION_ERROR = 'SOCKET_CONNECTION_ERROR'
let socketInstance: Nullable<Socket> = null
export type ChannelRO = { channel: Channel; response?: unknown }
export type SocketInstance = Socket & { channels: Channel[] }

export const PhoenixSocket = {
  connect(token: string, wsUrl: string, silent = false) {
    return new Promise((resolve, reject) => {
      if (this.connClosed()) {
        socketInstance = new Socket(wsUrl, {
          params: { token },
          // socket debug logger
          logger: (kind, msg, data) => {
            // eslint-disable-next-line no-console
            console.log('Socket log', `${kind}: ${msg}`, data)
          },
        })
        socketInstance.connect()
        this.onClose()
        // eslint-disable-next-line no-console
        console.log('Socket is connected!')
        resolve({ socketInstance })
      } else if (!this.connAvaiable()) {
        socketInstance?.connect()
        this.onClose()
        // eslint-disable-next-line no-console
        console.log('Socket reconnected! ')
        resolve({ socketInstance })
      } else if (!silent) {
        // eslint-disable-next-line no-console
        console.warn('Try to connect a connected socket.')
        reject({ socketInstance })
      }
    })
  },

  disconnect() {
    if (this?.connClosed()) {
      return
    }

    socketInstance?.disconnect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(socketInstance as any).reconnectTimer.reset()
      // eslint-disable-next-line no-console
      console.log('Socket disconnected!')
    })
  },

  connAvaiable() {
    return (
      socketInstance &&
      (socketInstance.connectionState() === 'open' ||
        socketInstance.connectionState() === 'connecting')
    )
  },

  connClosed() {
    return (
      isNull(socketInstance) || socketInstance?.connectionState() === 'closed'
    )
  },

  leavePreviousChannels(
    currentChannelId: string | number | undefined,
    prefix: SocketChannels
  ): Promise<ChannelRO> {
    return new Promise((resolve, reject) => {
      const topicName = `${prefix}:${currentChannelId}`
      const channels = filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (socketInstance as any).channels,
        (ch) => ch.topic !== topicName && ch.topic.includes(prefix)
      )
      if (channels?.length) {
        channels.forEach((channel) => {
          if (channel && channel.state !== 'closed') {
            //remove listneres
            channel.off(ConversationChannelEvents.MarkRead)
            channel.off(ConversationChannelEvents.MarkDelivered)

            channel
              ?.leave()
              ?.receive('ok', () => {
                // eslint-disable-next-line no-console
                console.log(`[Vue 3] Left ${channel.topic}`)
                resolve({ channel })
              })
              .receive('error', () => {
                // eslint-disable-next-line no-console
                console.error(`[Vue 3 Error] Left ${channel.topic}`)
                reject({ channel })
              })
          }
        })
      }
    })
  },

  isChannelSubscribed(id: string | number | undefined, prefix: SocketChannels) {
    if (this.connClosed()) {
      // eslint-disable-next-line no-console
      console.error('[Vue 3] NO_SOCKET_CONNECTION')
    }
    const topicName = `${prefix}:${id}`
    const channel = this.getCannelByName(topicName)

    return (
      (channel && (channel?.state as ChannelState) === 'joined') ||
      (channel?.state as ChannelState) === 'joining'
    )
  },

  findChannel<ID extends string | number | undefined>(
    id: ID,
    prefix: SocketChannels,
    onChangeChannelState: (state: ChannelState, id: ID) => void = noop
  ): Promise<ChannelRO> {
    return new Promise((resolve, reject) => {
      if (this.connClosed()) {
        // eslint-disable-next-line no-console
        console.error('[Vue 3] No socket connection, please connect first')
        reject('NO_SOCKET_CONNECTION')
      }

      const topicName = `${prefix}:${id}`
      let channel = this.getCannelByName(topicName)

      if (!channel) {
        channel = socketInstance?.channel(topicName)
      }

      if (channel?.state === 'closed') {
        channel.onClose(() => {
          onChangeChannelState(channel.state, id)
        })

        channel.onError(() => {
          onChangeChannelState(channel.state, id)
          reject(SOCKET_CONNECTION_ERROR)
        })

        channel
          .join()
          .receive('ok', (response: unknown) => {
            // eslint-disable-next-line no-console
            console.log(`[Vue 3] Joined ${channel.topic}`)

            onChangeChannelState(channel.state, id)
            resolve({ channel, response })
          })
          .receive('error', (error: unknown) => {
            // eslint-disable-next-line no-console
            console.error(
              `[Vue 3 Error] Unable to join ${channel.topic}`,
              error
            )

            onChangeChannelState(channel.state, id)
            reject(error)
          })
      } else {
        resolve({ channel })
      }
    })
  },

  leaveChannel(
    id: string | number | undefined,
    prefix: SocketChannels
  ): Promise<ChannelRO> {
    return new Promise((resolve, reject) => {
      if (this.connClosed()) {
        // eslint-disable-next-line no-console
        console.error('No socket connection, please connect first')
        reject('NO_SOCKET_CONNECTION')
      }

      const topicName = `${prefix}:${id}`
      const channel = this.getCannelByName(topicName)

      if (channel?.state === 'closed') {
        reject()
      } else {
        channel
          ?.leave()
          ?.receive('ok', () => {
            // eslint-disable-next-line no-console
            console.log(`[Vue 3] Left ${channel.topic}`)
            resolve({ channel })
          })
          .receive('error', () => {
            // eslint-disable-next-line no-console
            console.error(`[Vue 3Error] Left ${channel.topic}`)
            reject({ channel })
          })
      }
    })
  },

  leaveAllChannelTopics(channelName: SocketChannels) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const channels = filter((socketInstance as any)?.channels, (ch: Channel) =>
      ch.topic.startsWith(channelName)
    )
    const leave = (channel: Channel) =>
      new Promise((resolve, reject) => {
        channel?.leave().receive('ok', resolve).receive('error', reject)
      })
    return Promise.all(channels.map(leave))
  },

  getCannelByName(topicName: string) {
    return find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (socketInstance as any).channels,
      (ch) => ch.topic === topicName
    )
  },

  onClose() {
    socketInstance?.onClose(() => {
      this.disconnect()
    })
    socketInstance?.onError((payload) => {
      // eslint-disable-next-line
      console.error('WebSocket connection error:', payload)

      if (
        ((payload as unknown as AxiosError).code as unknown as HttpStatus) !==
        HttpStatus.FORBIDDEN
      ) {
        this.disconnect()
      }
    })
  },
}

// receive connection and params by options
// https://vuejs.org/v2/guide/plugins.html
// export default function install(Vue) {
//   Object.defineProperty(Vue.prototype, '$socket', {
//     get() {
//       return socketInstance
//     },
//   })
// }
